import { cn } from '@bs/ui-components'
import { Check, Search, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

export interface SearchPickerOption {
  id: string
  label: string
  /** Passthrough for whatever the caller's source data was (e.g. a full `CityItem`, `country`
   * included) — `onPick` callers that need more than id+label read it from here rather than the
   * picker re-fetching or the caller re-deriving it from a label lookup. */
  data?: unknown
  /** A logo/avatar for this result — legacy's `Suggest` `showMenuImages`/`menuItemImgRenderer`
   * (company search AND the "School" field, which reuses company search for institutions). Only
   * company-backed pickers set this; anything else (cities, skills, degree vocab) has none. */
  logoUrl?: string
}

/**
 * Two pickers this screen needs turned out to be one component with a data-source switch:
 *
 *   static mode (`options`)   preload the whole vocabulary, filter client-side as the user
 *                              types — right for anything small enough to hold in memory
 *                              (seniority levels, roles, skills, industries: tens to low
 *                              thousands of rows).
 *   remote mode (`onSearch`)  debounce, call out, show nothing until there's a query — right
 *                              for anything too large or too dynamic to preload (world cities;
 *                              matches legacy's `useLocationsSearchQuery`, which never loaded
 *                              its full table either).
 *
 * `multiple={false}` replaces the selection instead of toggling it — a single-value field (e.g.
 * "which city do you live in", legacy's `Suggest`) shows its one pick INSIDE the input like a
 * normal combobox, never the tag-pill row `multiple` mode uses; growing a removable chip for a
 * field that can only ever hold one value read as "this is a multi-select" when it wasn't one.
 *
 * The results panel FLOATS (`absolute`) over whatever is below it rather than pushing it down —
 * a combobox that grows the page every time it has results is the wrong shape for a picker sitting
 * inside a fixed-height row (e.g. job-search-page.tsx's search bar): it visibly deforms the row
 * around it instead of overlaying, which reads as the UI breaking rather than a dropdown opening.
 */
export function SearchPicker({
  values,
  onChange,
  options,
  onSearch,
  onResultsSeen,
  onPick,
  onQueryChange,
  multiple = true,
  placeholder = 'Select...',
  debounceMs = 300,
  className,
}: {
  values: string[]
  onChange(values: string[]): void
  options?: SearchPickerOption[]
  onSearch?(query: string): Promise<SearchPickerOption[]>
  onResultsSeen?(options: SearchPickerOption[]): void
  /** Fires with the full option (id included) at the moment of selection — `onChange` only ever
   * carries labels, so a caller that needs the id (e.g. a single-select city field storing
   * `cityId`) reads it here instead of re-deriving it from a label lookup. */
  onPick?(option: SearchPickerOption): void
  /** Fires on every keystroke, unlike `onSearch`/`onPick` which only fire on a debounced query or
   * an actual selection. Lets a caller support a free-solo fallback (a value not in the catalog,
   * e.g. blocking a company we've never scraped) without this component needing its own concept
   * of "create new" — it just exposes the raw typed text. */
  onQueryChange?(query: string): void
  multiple?: boolean
  placeholder?: string
  debounceMs?: number
  /** Overrides the outer card's own classes (border/background/padding/height) — e.g. to match a
   * shorter sibling `<input>` in a search bar row. Merged with `tailwind-merge`, so passing e.g.
   * `p-2` here really does replace the default `p-3`, not just append alongside it. */
  className?: string
}) {
  const [query, setQuery] = useState('')
  const [remoteResults, setRemoteResults] = useState<SearchPickerOption[]>([])
  const [searching, setSearching] = useState(false)

  useEffect(() => {
    if (!onSearch) return
    const term = query.trim()
    if (!term) {
      setRemoteResults([])
      return
    }
    setSearching(true)
    const timer = window.setTimeout(() => {
      onSearch(term)
        .then((results) => {
          setRemoteResults(results)
          onResultsSeen?.(results)
        })
        .catch(() => setRemoteResults([]))
        .finally(() => setSearching(false))
    }, debounceMs)
    return () => window.clearTimeout(timer)
    // Deliberately keyed on `query` alone — onSearch/onResultsSeen are stable callbacks from the
    // parent, not deps that should restart the debounce.
  }, [query])

  const filteredStatic = useMemo(
    () => (options ? options.filter((option) => option.label.toLowerCase().includes(query.toLowerCase())) : []),
    [options, query],
  )
  const results = onSearch ? remoteResults : filteredStatic
  // Static mode shows its (unfiltered-until-typed) list immediately; remote mode shows nothing
  // until there's a query — the whole reason the two modes exist.
  const showResults = onSearch ? query.trim().length > 0 : true

  function pick(option: SearchPickerOption) {
    onPick?.(option)
    if (!multiple) {
      onChange([option.label])
      setQuery('')
      return
    }
    onChange(values.includes(option.label) ? values.filter((v) => v !== option.label) : [...values, option.label])
  }

  function clearSingle() {
    onChange([])
    setQuery('')
    onQueryChange?.('')
  }

  // Single mode shows its one pick INSIDE the input (like a normal combobox) once the query is
  // empty — typing anything starts a fresh search, same as clearing it first would.
  const inputValue = !multiple && !query && values[0] ? values[0] : query

  return (
    <div className="relative">
      <div className={cn('rounded-xl border border-input-border bg-input p-3 transition duration-200 focus-within:border-accent', className)}>
        {multiple && values.length ? (
          <div className="mb-3 flex flex-wrap gap-2">
            {values.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => onChange(values.filter((value) => value !== item))}
                className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-white"
              >
                {item}
                <X className="size-3" />
              </button>
            ))}
          </div>
        ) : null}
        <div className="relative">
          <Search className="absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={inputValue}
            onChange={(event) => { setQuery(event.target.value); onQueryChange?.(event.target.value) }}
            placeholder={placeholder}
            className="h-9 w-full border-0 bg-transparent pl-8 pr-8 text-base text-[#404040] outline-none placeholder:text-[#c7c7c7] focus:placeholder:text-transparent"
          />
          {!multiple && !query && values[0] ? (
            <button
              type="button"
              onClick={clearSingle}
              aria-label={`Clear ${placeholder}`}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-destructive"
            >
              <X className="size-4" />
            </button>
          ) : null}
        </div>
      </div>
      {showResults ? (
        // Remote (`onSearch`) mode is a transient, search-as-you-type combobox — its results
        // float over whatever is below rather than pushing it down. Static (`options`) mode is
        // an always-visible browsable checklist by design (job-preferences-editor.tsx's
        // seniority/industry/company-size pickers) — floating THAT would make it a permanently
        // stuck-open overlay sitting on top of whatever form field comes next, since it has no
        // open/closed state to begin with.
        <div className={onSearch
          ? 'absolute inset-x-0 top-[calc(100%+6px)] z-30 flex max-h-60 flex-col gap-1 overflow-y-auto rounded-xl border border-border bg-white p-2 shadow-lg'
          : 'mt-2 flex max-h-40 flex-col gap-1 overflow-y-auto border-t border-border pt-2'}>
          {searching ? (
            <p className="px-3 py-2 text-sm text-muted-foreground">Searching…</p>
          ) : results.length === 0 ? (
            <p className="px-3 py-2 text-sm text-muted-foreground">No matches</p>
          ) : (
            results.map((option) => {
              const selected = values.includes(option.label)
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => pick(option)}
                  className={`flex items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition ${selected ? 'bg-accent/[0.08] font-semibold text-primary' : 'text-[#555] hover:bg-muted'}`}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    {option.logoUrl ? (
                      <img src={option.logoUrl} alt="" className="size-6 shrink-0 rounded object-contain" />
                    ) : null}
                    <span className="truncate">{option.label}</span>
                  </span>
                  {selected ? <Check className="size-4 shrink-0 text-accent" /> : null}
                </button>
              )
            })
          )}
        </div>
      ) : null}
    </div>
  )
}
